import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    LinearProgress
} from '@mui/material';
import { Close, CreditCard, Bolt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

const InsufficientCreditsModal = ({ open, onClose, availableCredits, totalCredit, cost = 1 }) => {
    const navigate = useNavigate();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: 450,
                    borderRadius: 3,
                    p: 1,
                    textAlign: 'center'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pb: 1, pt: 3 }}>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
                >
                    <Close fontSize="small" />
                </IconButton>

                <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#fee2e2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                }}>
                    <CreditCard sx={{ color: '#ef4444', fontSize: 32 }} />
                </Box>
                <Typography variant="h6" fontWeight={700}>
                    Not Enough AI Credits
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pb: 3, px: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    You don't have enough AI credits to perform this action.
                    <br />
                    This action costs <Typography component="span" fontWeight={600} color="text.primary">{cost} AI credits</Typography>, but your current balance is <Typography component="span" fontWeight={600} color="#ef4444">{availableCredits || 0} credits</Typography>.
                </Typography>

                <Box sx={{ textAlign: 'left', mt: 3, mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Current balance</Typography>
                        <Typography variant="caption" fontWeight={600}>{availableCredits || 0} / {Math.max(50, totalCredit || 50)} credits</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={((availableCredits || 0) / Math.max(50, totalCredit || 50)) * 100}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: '#94a3b8',
                                borderRadius: 4,
                            }
                        }}
                    />
                </Box>
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        You need at least {cost} credits to use this feature.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', padding: '0 24px 24px', gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        borderRadius: 2,
                        color: '#64748b',
                        borderColor: '#e2e8f0',
                        textTransform: 'none',
                        px: { xs: 2, sm: 3 },
                        '&:hover': {
                            borderColor: '#cbd5e1',
                            bgcolor: '#f8fafc'
                        }
                    }}
                >
                    Maybe Later
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Bolt />}
                    onClick={() => {
                        onClose();
                        navigate('/dashboard/bills/?tab=1');
                    }}
                    sx={{
                        borderRadius: 2,
                        bgcolor: '#5c6bc0',
                        textTransform: 'none',
                        px: { xs: 2, sm: 3 },
                        '&:hover': {
                            bgcolor: '#3f51b5'
                        }
                    }}
                >
                    Buy AI Credits
                </Button>
            </DialogActions>
        </Dialog>
    );
};

InsufficientCreditsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    availableCredits: PropTypes.number,
    totalCredit: PropTypes.number,
    cost: PropTypes.number
};

export default InsufficientCreditsModal;
